var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import React from 'react';
import { createWrapperStyle, createTitleStyle, createSeparatorStyle, createArrowStyle, createElementStyle, nameStyle, subtitleStyle } from './createStyle';
import Typography from '../typography/index.react';

var useMediaQueryListener = function useMediaQueryListener(mq, listener) {
  React.useEffect(function () {
    if (window.matchMedia) {
      var mql_1 = window.matchMedia(mq);
      mql_1.addListener(listener);
      return function () {
        return mql_1.removeListener(listener);
      };
    }
  }, [mq]);
};

export var TabAccordion = function TabAccordion(_a) {
  var _b = _a.accordionMediaQuery,
      accordionMediaQuery = _b === void 0 ? '(max-width: 960px)' : _b,
      _c = _a.type,
      type = _c === void 0 ? 'accordion' : _c,
      className = _a.className,
      children = _a.children;
  var arrayOfChildNodes = React.Children.toArray(children);
  var initialTabIndex = arrayOfChildNodes.findIndex(function (item) {
    return Boolean(item.props.initial);
  });
  var initialTab = initialTabIndex === -1 ? null : initialTabIndex;

  var _d = React.useState(initialTab),
      currentTab = _d[0],
      setCurrentTab = _d[1];

  useMediaQueryListener(accordionMediaQuery, function (e) {
    if (!e.matches) {
      setCurrentTab(function (current) {
        return current === null ? initialTab : current;
      });
    }
  });

  var onButtonClick = function onButtonClick(newTab) {
    setCurrentTab(function (current) {
      if (current === newTab && window.matchMedia && window.matchMedia(accordionMediaQuery).matches) {
        return null;
      }

      return newTab;
    });
  };

  return React.createElement("div", {
    className: createWrapperStyle({
      accordionMediaQuery: accordionMediaQuery,
      className: className
    })
  }, React.Children.map(children, function (child, index) {
    var Component = child.props.as || 'button';
    return React.createElement(React.Fragment, {
      key: child.props.name
    }, React.createElement(Component, {
      onClick: function onClick() {
        return onButtonClick(index);
      },
      className: createTitleStyle({
        isAccordion: type === 'accordion',
        isActive: currentTab === index,
        accordionMediaQuery: accordionMediaQuery
      }),
      to: child.props.to || ''
    }, React.createElement(Typography, {
      className: nameStyle
    }, child.props.name), React.createElement(Typography, {
      className: subtitleStyle
    }, child.props.subtitle), React.createElement("svg", {
      className: createArrowStyle({
        isActive: currentTab === index,
        accordionMediaQuery: accordionMediaQuery
      }),
      viewBox: "0 0 32 32"
    }, React.createElement("path", {
      d: "M30.125 6.688L16 21.438 1.875 6.688 0 8.563l16 16.75 16-16.75z"
    }))), React.cloneElement(child, {
      isActive: currentTab === index,
      accordionMediaQuery: accordionMediaQuery
    }));
  }), React.createElement("hr", {
    className: createSeparatorStyle({
      accordionMediaQuery: accordionMediaQuery
    }),
    "aria-hidden": true
  }));
};

var usePrevious = function usePrevious(value) {
  var ref = React.useRef(undefined);
  React.useEffect(function () {
    ref.current = value;
  });
  return ref.current;
};

var useSmoothOpen = function useSmoothOpen(isActive, mq) {
  if (mq === void 0) {
    mq = '';
  }

  var ref = React.useRef(undefined);

  var _a = React.useState({}),
      style = _a[0],
      setStyle = _a[1];

  var heightRef = React.useRef(0);
  var prevIsActive = usePrevious(isActive);
  heightRef.current = React.useMemo(function () {
    if (ref.current) return ref.current.getBoundingClientRect().height;
    return 0;
  }, [ref.current, isActive]);
  React.useLayoutEffect(function () {
    if (!(window.matchMedia && window.matchMedia(mq).matches)) return;

    if (isActive && !prevIsActive) {
      setStyle(function (s) {
        return __assign(__assign({}, s), {
          maxHeight: 0,
          transition: 'none'
        });
      });
      setTimeout(function () {
        setStyle(function (s) {
          return __assign(__assign({}, s), {
            maxHeight: '100vh',
            transition: null
          });
        });
      }, 20);
      setTimeout(function () {
        setStyle({});
      }, 1000);
    }

    if (!isActive && prevIsActive) {
      setStyle(function (s) {
        return __assign(__assign({}, s), {
          maxHeight: heightRef.current + "px",
          transition: 'none'
        });
      });
      setTimeout(function () {
        setStyle({});
      }, 20);
    }
  }, [isActive, prevIsActive, ref.current, heightRef.current]);
  return {
    style: style,
    ref: ref
  };
};

export var Tab = function Tab(_a) {
  var isActive = _a.isActive,
      accordionMediaQuery = _a.accordionMediaQuery,
      className = _a.className,
      initial = _a.initial,
      name = _a.name,
      subtitle = _a.subtitle,
      as = _a.as,
      props = __rest(_a, ["isActive", "accordionMediaQuery", "className", "initial", "name", "subtitle", "as"]);

  var smoothProps = useSmoothOpen(Boolean(isActive), accordionMediaQuery);
  return React.createElement("div", __assign({}, props, smoothProps, {
    className: createElementStyle({
      accordionMediaQuery: accordionMediaQuery,
      isActive: isActive,
      className: className
    })
  }));
};
export var Tabs = function Tabs(props) {
  return React.createElement(TabAccordion, __assign({}, props, {
    type: "tab",
    accordionMediaQuery: "(max-width: 0px)"
  }));
};
export var Accordion = function Accordion(props) {
  return React.createElement(TabAccordion, __assign({}, props, {
    type: "accordion",
    accordionMediaQuery: "(min-width: 0px)"
  }));
};